import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { AllCommunityModule, ModuleRegistry, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import myTheme from "../../../style/agGridTheme/theme";
import { toast } from "sonner";
// pour restreaindre l'accès à ce composant
import withPermission from "../../../core/common/autorisations/withPermission";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import InfosAlertYearDisabled from "../../../core/common/alerts/infosAlertYearDisabled";
import FinanceMenu from "../financeMenu";
import InfoStudentPaye from "./InfoStudentPaye";
import CreatePaymentModal from "./CreatePaymentModal";
import {
  getFinancialStudentSummary,
  getStudentPaymentByYear,
} from "../../../api/studentPaiement";
import { getStudentPaiementPlans } from "../../../api/studentPaiementPlan";
import { getAllPaymentPlans } from "../../../api/paiementPlans";
import { getPaymentSettingsByYear } from "../../../api/paiement_settings";
import { formatFinancialSummaryStudent } from "./financialSummary";

// AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Données fictives pour visualisation immédiate
interface PaymentRow {
  id: string;
  utilisateur: string;
  classe: string;
  plan: string;
  credit: number;
  non_paye: number;
  somme_attendue: number;
  paiements_entrants: number;
  paiement_trop_recu: number;
}

// boutton protégée pour créer un nouveau payment
const ProtectedBtnAddPayment = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => {
          props.setShowCreate(true);
        }}
        //   to="#"
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-square-rounded-plus me-2" />
        Nouveau Paiement
      </button>
    </div>
  ),
  [buildPermission(Resource.LEVEL, Action.CREATE)],
  "masquer",
);

const PaymentList = () => {
  const { selectedYear } = useAcademicYear();

  //Initialisation des state utilisés
  const [allDataPayment, setAllDataPayment] = useState<any[]>([]);
  const [allPaymentPlans, setAllPaymentPlans] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [showCreate, setShowCreate] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);

  const [showInfo, setShowInfo] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>();

  const [paymentSettings, setPaymentSettings] = useState<any>();

  // Colonnes du tableau AG Grid
  const defaultColDef = useMemo(() => {
    return {
      filter: true,
      resizable: true,
      sortable: true,
    } as ColDef;
  }, []);

  const colDefs: ColDef<PaymentRow>[] = [
    {
      field: "utilisateur",
      headerName: "Utilisateur",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: any) => {
        return (
          <Link
            to="#"
            className="text-primary"
            onClick={(e) => {
              e.preventDefault();
              openStudentDetails(params.data);
              setShowInfo(true);
            }}
          >
            {params.value}
          </Link>
        );
      },
    },
    {
      field: "classe",
      headerName: "Classe",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "credit",
      headerName: "Crédit",
      width: 130,
      valueFormatter: (p: any) => (p.value ?? 0).toLocaleString("fr-FR"),
      cellClass: (p: any) =>
        p.value < 0 ? "text-danger fw-semibold" : "text-success fw-semibold",
    },
    {
      field: "non_paye",
      headerName: "Non payé",
      width: 130,
      valueFormatter: (p: any) => (p.value ?? 0).toLocaleString("fr-FR"),
    },
    {
      field: "somme_attendue",
      headerName: "Somme attendue",
      width: 150,
      valueFormatter: (p: any) => (p.value ?? 0).toLocaleString("fr-FR"),
    },
    {
      field: "paiements_entrants",
      headerName: "Paiements entrants",
      width: 160,
      valueFormatter: (p: any) => (p.value ?? 0).toLocaleString("fr-FR"),
    },
    {
      field: "paiement_trop_recu",
      headerName: "Paiement en trop reçu",
      width: 180,
      valueFormatter: (p: any) => (p.value ?? 0).toLocaleString("fr-FR"),
    },
  ];

  // Filtre par plan de paiement (multi-sélection façon Restriction)
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [classSearchText, setClassSearchText] = useState<string>(""); // état de recherche pour les classes

  const togglePlan = (id: string) => {
    setSelectedPlans((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  };

  const toggleClass = (classe: string) => {
    setSelectedClasses((prev) =>
      prev.includes(classe)
        ? prev.filter((item) => item !== classe)
        : [...prev, classe],
    );
  };

  const classOptions = useMemo(() => {
    const classes = allDataPayment
      .map((studentData: any) => studentData?.classe)
      .filter(Boolean);
    return Array.from(new Set(classes)).sort((a, b) =>
      String(a).localeCompare(String(b), "fr"),
    );
  }, [allDataPayment]);

  // Filtrer les classes basé sur le texte de recherche
  const filteredClassOptions = useMemo(() => {
    if (!classSearchText) return classOptions;
    return classOptions.filter((classe: string) =>
      String(classe)
        .toLowerCase()
        .includes(classSearchText.toLowerCase()),
    );
  }, [classOptions, classSearchText]);

  const filteredRows = useMemo(() => {
    const computedRows = computePayment(allDataPayment) || [];
    return computedRows.filter((studentData) => {
      const matchPlan =
        selectedPlans.length === 0 ||
        studentData?.plan.some((plan: any) =>
          selectedPlans.includes(plan?.PaymentPlan?.id || ""),
        );
      const matchClass =
        selectedClasses.length === 0 ||
        selectedClasses.includes(studentData?.classe || "");

      return matchPlan && matchClass;
    });
  }, [selectedPlans, selectedClasses, allDataPayment]);

  const pinnedBottomRowData = useMemo(() => {
    const sum = (arr: PaymentRow[], key: keyof PaymentRow) =>
      arr.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
    const totalCredit = sum(filteredRows, "credit");
    const totalNonPaye = sum(filteredRows, "non_paye");
    const totalSomme = sum(filteredRows, "somme_attendue");
    const totalEntrants = sum(filteredRows, "paiements_entrants");
    const totalTrop = sum(filteredRows, "paiement_trop_recu");

    return [
      {
        id: "sum",
        utilisateur: "Somme:",
        classe: "",
        plan: "",
        credit: totalCredit,
        non_paye: totalNonPaye,
        somme_attendue: totalSomme,
        paiements_entrants: totalEntrants,
        paiement_trop_recu: totalTrop,
      } as PaymentRow,
    ];
  }, [filteredRows]);

  // fonction pour éffectuer les calculs des payments
  function computePayment(allPaymentData: any[]) {
    // vérifier si des plans de paiement ont été sélectionnés
    if (allPaymentData.length <= 0) return;

    if (selectedPlans.length > 0) {
      // calculer les sommes pour tous les plans de payment sélectionnés
      const paymentByStudent = allPaymentData.map((studentData: any) => {
        // calcul de la somme attendue qui est la somme des montant des plan émarger
        const somme_attendue = studentData?.plan?.reduce(
          (acc: number, plan: any) => {
            // si le plan n'est pas sélectionné, on ne l'inclut pas dans la somme attendue
            if (
              !selectedPlans.some((planId) => plan?.PaymentPlan?.id === planId)
            )
              return acc;

            // calculer mes rabais qui s'applique à ce plan
            const discountAmount = studentData.myDiscounts.reduce(
              (acc: number, discount: any) => {
                const matched = discount.repartitions.filter(
                  (rep: any) => rep?.student_repartition_id === plan?.id,
                );
                const total_amount = matched.reduce(
                  (acc: number, rep: any) => acc + rep?.repartition_amount,
                  0,
                );
                return acc + total_amount;
              },
              0,
            );

            return (
              acc + Number(plan?.PaymentPlan?.amount || 0) - discountAmount
            );
          },
          0,
        );

        const paiements_entrants = studentData?.plan?.some((plan: any) =>
          selectedPlans.some((planId) => plan?.PaymentPlan?.id === planId),
        )
          ? studentData.payments.reduce((acc: number, payment: any) => {
              const matchedPaymentPlans = studentData.plan.filter((plan: any) =>
                selectedPlans.some(
                  (planId) => plan?.PaymentPlan?.id === planId,
                ),
              );

              const montantReparti = (payment.repartitions || []).reduce(
                (sum: number, rep: any) =>
                  matchedPaymentPlans.some(
                    (plan: any) => plan.id === rep.payment_plan?.id,
                  )
                    ? sum + (rep.amount || 0)
                    : sum,
                0,
              );
              // return acc + (payment.amount || 0) + montantReparti
              return acc + montantReparti;
            }, 0)
          : 0;

        const paiement_trop_recu =
          paiements_entrants < somme_attendue
            ? 0
            : paiements_entrants - somme_attendue;
        const non_paye =
          somme_attendue > paiements_entrants
            ? Math.abs(paiements_entrants - somme_attendue)
            : 0;
        const credit = paiements_entrants - somme_attendue;
        return {
          ...studentData,
          somme_attendue,
          paiements_entrants,
          paiement_trop_recu,
          non_paye,
          credit,
        };
      });

      return paymentByStudent;
    }
    // calcluler les sommes pour touts les plans de payment qui ont été attribuées à chaque étudiants
    const paymentByStudent = allPaymentData.map((studentData: any) => {
      // calcul de la somme attendue qui est la somme des montant des plan émarger
      const somme_attendue = studentData.plan.reduce(
        (acc: number, plan: any) => {
          const dueDate = new Date(plan?.PaymentPlan?.due_date);
          const today = new Date();
          // calculer mes rabais qui s'applique à ce plan
          const discountAmount = studentData.myDiscounts.reduce(
            (acc: number, discount: any) => {
              const matched = discount.repartitions.filter(
                (rep: any) => rep?.student_repartition_id === plan?.id,
              );
              const total_amount = matched.reduce(
                (acc: number, rep: any) => acc + rep?.repartition_amount,
                0,
              );
              return acc + total_amount;
            },
            0,
          );
          // console.log("Total amount dicsount for this plan: ", discountAmount);
          return today >= dueDate
            ? acc + Number(plan?.PaymentPlan?.amount || 0) - discountAmount
            : acc;
        },
        0,
      );

      const paiements_entrants = studentData.payments.reduce(
        (acc: number, payment: any) => {
          const montantReparti = (payment.repartitions || []).reduce(
            (sum: number, rep: any) => sum + (rep.amount || 0),
            0,
          );
          // return acc + (payment.amount || 0) + montantReparti
          return acc + montantReparti;
        },
        0,
      );
      const paiement_trop_recu =
        paiements_entrants < somme_attendue
          ? 0
          : paiements_entrants - somme_attendue;
      const non_paye =
        somme_attendue > paiements_entrants
          ? Math.abs(paiements_entrants - somme_attendue)
          : 0;
      const credit = paiements_entrants - somme_attendue;
      return {
        ...studentData,
        somme_attendue,
        paiements_entrants,
        paiement_trop_recu,
        non_paye,
        credit,
      };
    });
    // console.log('PayementByStudent: ', paymentByStudent)
    return paymentByStudent;
  }

  const openStudentDetails = async (student: any) => {
    setSelectedStudent(student);
    if (!selectedYear?.id || !student?.id) return;

    try {
      const permissionContext = buildPermissionContext(
        Resource.PAYMENT,
        Action.READ,
      );
      const [paymentResponse, planResponse] = await Promise.all([
        getStudentPaymentByYear(student.id, selectedYear.id, permissionContext),
        getStudentPaiementPlans(student.id, selectedYear.id, permissionContext),
      ]);
      setSelectedStudent((current: any) =>
        current?.id === student.id
          ? {
              ...current,
              plan: planResponse?.data || current.plan || [],
              payments: paymentResponse?.data?.student?.payments || [],
            }
          : current,
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Impossible de charger le détail financier de cet étudiant.",
      );
    }
  };

  // fonction pour récupérer les paiementsen fonction de l'année académique
  const fetchAllDataPaymentByYear = async () => {
    // Lors d'un rafraîchissement, garder le tableau courant visible.
    setIsLoading(true);
    try {
      if (!selectedYear) return [];
      const permissionContext = buildPermissionContext(
        Resource.PAYMENT,
        Action.READ,
      );

      const response = await getFinancialStudentSummary(
        selectedYear.id,
        permissionContext,
      );
      const formattedData = (response?.data || []).map(
        formatFinancialSummaryStudent,
      );

      setAllDataPayment(formattedData);
      return computePayment(formattedData) || [];

      // setAllDataPayment(res_pay.data);
    } catch (error: any) {
      // console.log('Erreur lors de la récupération des paiements:', error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
      return [];
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  const refreshPaymentsAndSelectedStudent = async () => {
    const updatedData = await fetchAllDataPaymentByYear();
    if (!selectedStudent?.id) return;

    const updatedStudent = updatedData.find(
      (student: any) => student.id === selectedStudent.id,
    );
    if (updatedStudent) {
      openStudentDetails(updatedStudent);
    }
  };

  // fonction pour charger tous les plans de payment
  const fetchAllPaymentPlan = async () => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;

    try {
      const paymentPlans = await getAllPaymentPlans(
        selectedYear?.id,
        buildPermissionContext(Resource.PAYMENT, Action.READ),
      );
      // console.log("All payment plans: ", paymentPlans.data);
      setAllPaymentPlans(paymentPlans.data);
    } catch (error: any) {
      // console.log('Erreur lors du chargement des plans de payment: ', error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour récupérer les paramêtres de paiement de l'année academique
  const fetchPaymentParameters = async () => {
    try {
      if (!selectedYear) return;
      const res = await getPaymentSettingsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.PAYMENT, Action.READ),
      );
      // console.log("Parameters: ", res.data);
      setPaymentSettings(res?.data[0]);
    } catch (error: any) {
      // console.log("Error fetching payment parameters:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    fetchAllPaymentPlan();
    fetchAllDataPaymentByYear();
    fetchPaymentParameters();
  }, [selectedYear]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between ">
            <FinanceMenu />
          </div>
          {!selectedYear?.statut && <InfosAlertYearDisabled />}

          {/* Boutton en entête de la page */}

          <div className="bg-white p-3 border rounded-1 d-flex align-items-center justify-content-between flex-wrap mb-4 pb-0">
            <h4 className="mb-3">Paiements des étudiants</h4>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
              <div className="dropdown">
                <button
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  style={{ minWidth: "200px" }}
                >
                  <i className="ti ti-wallet me-2" />
                  {selectedPlans.length > 0
                    ? `${selectedPlans.length} plan(s)`
                    : "Plans de paiement"}
                </button>

                <ul
                  className="dropdown-menu p-2"
                  onClick={(e) => e.stopPropagation()}
                  style={{ minWidth: "220px" }}
                >
                  {allPaymentPlans.map((plan: any) => (
                    <li key={plan.id} className="mb-2">
                      <label
                        className={`dropdown-item d-flex flex-column ${
                          selectedPlans.includes(plan.id)
                            ? "bg-light text-primary rounded-1"
                            : ""
                        }`}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={selectedPlans.includes(plan.id)}
                            onChange={() => togglePlan(plan.id)}
                          />
                          <span>{plan.label}</span>
                        </div>
                        <div className="ms-4">
                          <small className="text-muted">
                            {plan.amount.toLocaleString("fr-FR")}{" "}
                            {paymentSettings?.currency || "FCFA"}
                          </small>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
                <div className="dropdown">
                <button
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  style={{ minWidth: "200px" }}
                >
                  <i className="ti ti-school me-2" />
                  {selectedClasses.length > 0
                  ? `${selectedClasses.length} classe(s)`
                  : "Classes"}
                </button>

                <ul
                  className="dropdown-menu p-2"
                  onClick={(e) => e.stopPropagation()}
                  style={{ minWidth: "220px", maxHeight: "300px", overflowY: "auto", overflowX: "hidden" }}
                >
                  <li className="mb-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Rechercher une classe..."
                      value={classSearchText}
                      onChange={(e) => setClassSearchText(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </li>
                  {filteredClassOptions.slice() // pour éviter de modifier directement la réponse
                  .sort((a: any, b: any) => {
                    // Tri par Subject.label
                    // console.log("Label du sujet: ",a)
                    return a?.localeCompare(b, "fr", { numeric: true });
                  }).map((classe: string) => (
                  <li key={classe} className="mb-2">
                    <label
                    className={`dropdown-item d-flex align-items-center gap-2 ${
                      selectedClasses.includes(classe)
                      ? "bg-light text-primary rounded-1"
                      : ""
                    }`}
                    style={{ cursor: "pointer" }}
                    >
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={selectedClasses.includes(classe)}
                      onChange={() => toggleClass(classe)}
                    />
                    <span>{classe}</span>
                    </label>
                  </li>
                  ))}
                </ul>
                </div>

              <ProtectedBtnAddPayment setShowCreate={setShowCreate} />
            </div>
          </div>

          {/* /Page Header */}

          {/* Students List (On affiche le tableau qui si les données son chargées même vide) */}
          <div
            style={{
              width: "100%",
              height: "620px",
            }}
          >
            <AgGridReact
              rowData={filteredRows}
              loading={isLoading && !hasLoaded}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={pageSize}
              rowSelection="multiple"
              domLayout="normal"
              gridOptions={{
                theme: myTheme,
              }}
              pinnedBottomRowData={pinnedBottomRowData}
              getRowStyle={(params) => {
                if (params.node.rowPinned === "bottom") {
                  return { fontWeight: "bold" };
                }
                return undefined;
              }}
            />
          </div>
          {/* /Students List */}
          <InfoStudentPaye
            show={showInfo}
            student={selectedStudent}
            onClose={() => setShowInfo(false)}
            onSave={() => {
              refreshPaymentsAndSelectedStudent();
            }}
            currency={paymentSettings?.currency}
          />
          <CreatePaymentModal
            show={showCreate}
            onClose={() => setShowCreate(false)}
            students={allDataPayment}
            onSaved={() => {
              fetchAllDataPaymentByYear();
            }}
            currency={paymentSettings?.currency}
          />
        </div>
      </div>
    </>
  );
};

export default withPermission(
  PaymentList,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.PAYMENT, action),
    ),
  ],
  "masquer",
);
